import { isSignedImageUrlUsable } from './imageCache';

const STORAGE_KEY = 'wisefriend:image-url-aliases:v1';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 64;
const MAX_URL_LENGTH = 8192;
const MAX_STORAGE_LENGTH = MAX_ENTRIES * (MAX_URL_LENGTH + 128);

interface ImageAlias {
  url: string;
  storedAt: number;
}

const identityFor = (source: string): string | null => {
  try {
    if (source.length > MAX_URL_LENGTH) return null;
    const url = new URL(source);
    const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if ((url.protocol !== 'https:' && !localHttp) || url.username || url.password) return null;
    if (!url.searchParams.get('X-Amz-Signature') && !url.searchParams.get('Signature')) return null;
    const controls = url.searchParams.getAll('response-cache-control');
    if (controls.length !== 1) return null;
    const directives = controls[0].split(',').map((part) => part.trim().toLowerCase());
    const revisions = directives.filter((part) => /^wf-image-revision\s*=/.test(part));
    const ages = directives.filter((part) => /^max-age\s*=/.test(part));
    if (revisions.length !== 1 || ages.length !== 1 || !directives.includes('private') ||
        directives.some((part) => /^(public|no-cache|no-store)(?:$|=)/.test(part))) return null;
    const maxAge = ages[0].match(/^max-age=(\d+)$/)?.[1];
    if (!maxAge || !Number.isSafeInteger(Number(maxAge)) || Number(maxAge) < MAX_AGE_MS / 1000) return null;
    const revision = revisions[0].match(/^wf-image-revision=([a-f0-9]{64})$/)?.[1];
    if (!revision) return null;
    // Signature rotation and HTTP freshness do not change image bytes. Keep
    // other response parameters, origin and the encoded object path in identity.
    const parameters = Array.from(url.searchParams.entries()).filter(([name]) =>
      !/^x-amz-/i.test(name) &&
      !/^(signature|expires|awsaccesskeyid|securitytoken|response-cache-control)$/i.test(name),
    );
    parameters.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
    return JSON.stringify([url.origin, url.pathname, revision, parameters]);
  } catch {
    return null;
  }
};

export const hasPersistentImageRevision = (source: string): boolean =>
  isSignedImageUrlUsable(source) && identityFor(source) !== null;

const parseAliases = (raw: string | null): ImageAlias[] => {
  if (!raw || raw.length > MAX_STORAGE_LENGTH) return [];
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) return [];
  const now = Date.now();
  const entries = parsed.filter((value): value is ImageAlias =>
    value !== null && typeof value === 'object' &&
    typeof value.url === 'string' && identityFor(value.url) !== null &&
    Number.isSafeInteger(value.storedAt) && value.storedAt >= 0 &&
    value.storedAt <= now && now - value.storedAt < MAX_AGE_MS,
  ).map(({ url, storedAt }) => ({ url, storedAt }));
  entries.sort((a, b) => a.storedAt - b.storedAt);
  const seen = new Set<string>();
  return entries.filter(({ url }) => {
    const identity = identityFor(url)!;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  }).slice(-MAX_ENTRIES);
};

/**
 * Keep a transport URL stable so the browser can reuse its private HTTP image
 * cache across reloads and signature rotations. The identity is the object and
 * revision, never the signed URL. No application login token is stored here.
 *
 * The caller must supply a currently usable signature. A retained URL may have
 * expired while its downloaded bytes remain fresh in the browser cache. If the
 * image fails (e.g. cache eviction), forget it and retry the current source.
 */
export const getPersistentImageUrl = (source: string): string => {
  if (!hasPersistentImageRevision(source)) return source;
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    let aliases: ImageAlias[];
    try { aliases = parseAliases(raw); } catch { aliases = []; }
    const identity = identityFor(source);
    const existing = aliases.find(({ url }) => identityFor(url) === identity);
    if (existing) {
      const cleaned = JSON.stringify(aliases);
      if (cleaned !== raw) globalThis.localStorage.setItem(STORAGE_KEY, cleaned);
      return existing.url;
    }
    aliases.push({ url: source, storedAt: Date.now() });
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(aliases.slice(-MAX_ENTRIES)));
  } catch {
    // Blocked storage, quota and malformed records must not prevent image loading.
  }
  return source;
};

/** Evict only this object revision before retrying the current signed URL. */
export const forgetPersistentImage = (source: string): void => {
  const identity = identityFor(source);
  if (!identity) return;
  try {
    const aliases = parseAliases(globalThis.localStorage.getItem(STORAGE_KEY));
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(aliases.filter(({ url }) => identityFor(url) !== identity)));
  } catch {
    // Direct-image recovery still works when storage is unavailable.
  }
};
